/**
 * Recently viewed products component
 * @module recently-viewed
 * @version 1.0.0
 */

customElements.component = 'recently-viewed';

if (!customElements.get(customElements.component)) {
    // Auto-execute tracker for product pages
    (function () {
        if (!window.location.pathname.includes('/products/')) return;

        const pathParts = window.location.pathname.split('/');
        const productsIndex = pathParts.indexOf('products');
        if (productsIndex === -1 || pathParts.length <= productsIndex + 1) return;

        const handle = pathParts[productsIndex + 1].split('?')[0];
        if (!handle) return;

        const saveProduct = () => {
            try {
                const storageKey = 'recentlyViewedProduct-production';
                let productList = [];
                try {
                    const localData = localStorage.getItem(storageKey);
                    if (localData) productList = JSON.parse(localData).filter(h => h !== handle);
                } catch (e) { }

                productList.unshift(handle);
                if (productList.length > 8) productList = productList.slice(0, 8);
                localStorage.setItem(storageKey, JSON.stringify(productList));
            } catch (e) { }
        };

        document.readyState === 'loading'
            ? document.addEventListener('DOMContentLoaded', saveProduct)
            : saveProduct();
    })();

    class recentlyViewed extends HTMLElement {
        constructor() {
            super();
            this.debug = true;
            this.storageKey = 'recentlyViewedProduct-production';
            this.currentProductHandle = this.dataset.rvProductHandle || "";
            this.universalMode = this.dataset.universalMode === "true";

            document.readyState === 'loading'
                ? document.addEventListener('DOMContentLoaded', () => this.initialize())
                : setTimeout(() => this.initialize(), 100);
        }

        async initialize() {
            console.log('[RV] Initializing component');
            const productHandles = this.loadProductHandles();
            if (!productHandles.length) {
                console.log('[RV] No products to show');
                this.classList.add("hidden");
                return;
            }

            try {
                // 1. Fetch products
                const language = document.documentElement.lang?.trim() || 'en';
                const pathName = window.location.pathname;
                const pathParts = "/" + pathName.split('/')[1];
                let pathLanguage = '';

                if (language !== 'en') {
                    pathLanguage = pathParts;
                }

                const products = await this.fetchProducts(productHandles, pathLanguage, language);
                // console.log('[RV] Fetched products:', products);
                if (!products) {
                    this.classList.add("hidden");
                    return;
                }

                // 2. Render product cards
                const renderSuccess = this.renderProductCards(products);

                if (!renderSuccess) {
                    this.classList.add("hidden");
                    return;
                }

                // 3. Init Swiper AFTER render
                setTimeout(() => {
                    this.initSwiper();
                    this.classList.remove("hidden");
                }, 100);

            } catch (error) {
                console.warn('[RV] Error initializing:', error);
                this.classList.add("hidden");
            }
        }

        loadProductHandles() {
            try {
                const data = localStorage.getItem(this.storageKey);
                if (!data) return [];

                let productHandles = JSON.parse(data);

                // Filter out current product if not in universal mode
                if (!this.universalMode && this.currentProductHandle) {
                    productHandles = productHandles.filter(h => h !== this.currentProductHandle);
                }

                // console.log('[RV] Loaded handles:', productHandles);
                return productHandles;
            } catch (e) {
                console.error('[RV] Error loading handles:', e);
                return [];
            }
        }

        isInvalidProductResponse(responseText) {
            return /Liquid error/i.test(responseText);
        }

        parseProductSlide(responseText) {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const slideEl = html.querySelector('.swiper-slide[swiper-slide-template][data-rv-product-valid="true"]');

            if (!slideEl || !slideEl.querySelector('[data-product-card], product-card')) {
                return null;
            }

            return slideEl.outerHTML;
        }

        removeHandlesFromStorage(handles) {
            if (!handles.length) return;

            try {
                const data = localStorage.getItem(this.storageKey);
                if (!data) return;

                const productList = JSON.parse(data).filter((handle) => !handles.includes(handle));
                localStorage.setItem(this.storageKey, JSON.stringify(productList));
            } catch (e) {
                console.warn('[RV] Error pruning stale handles:', e);
            }
        }

        async fetchProducts(handles, pathLanguage, language) {
            const baseUrl = window.location.origin;
            const invalidHandles = [];

            const fetchPromises = handles.map((handle) =>
                fetch(`${baseUrl}${pathLanguage}/collections/${language}/products/${handle}?section_id=product-recently-viewed`)
                    .then((response) => {
                        if (!response.ok) {
                            if (response.status === 404) {
                                invalidHandles.push(handle);
                                if (this.debug) {
                                    console.warn(`[RV] Removing missing product handle: ${handle}`);
                                }
                                return null;
                            }
                            throw new Error(`HTTP error! status: ${response.status}`);
                        }
                        return response.text();
                    })
                    .then((responseText) => {
                        if (responseText === null) return null;

                        if (this.isInvalidProductResponse(responseText)) {
                            invalidHandles.push(handle);
                            return null;
                        }

                        const productCard = this.parseProductSlide(responseText);

                        if (!productCard) {
                            invalidHandles.push(handle);
                            if (this.debug) {
                                console.warn(`[RV] Skipping invalid or unavailable product handle: ${handle}`);
                            }
                            return null;
                        }

                        return productCard;
                    })
                    .catch((error) => {
                        console.error(`[RV] Error fetching product ${handle}:`, error);
                        return null;
                    })
            );

            const results = await Promise.allSettled(fetchPromises);

            if (invalidHandles.length) {
                this.removeHandlesFromStorage(invalidHandles);
            }

            const products = results
                .filter((result) => result.status === 'fulfilled' && result.value !== null)
                .map((result) => result.value)
                .join('\n');

            return products;
        }

        renderProductCards(products) {
            const fatherElement = this.querySelector('.swiper-wrapper');

            if (!fatherElement) {
                console.error('Swiper wrapper not found');
                return false;
            }

            fatherElement.innerHTML = products;

            if (!fatherElement.children.length) {
                console.warn('No products to display after rendering');
                return false;
            }

            return true;
        }

        initSwiper() {
            try {
                const swiperEl = this.querySelector('[data-swiper]');
                if (!swiperEl || typeof Swiper === 'undefined') {
                    return;
                }

                this.swiper = new Swiper(swiperEl, {
                    slidesPerView: 1.23,
                    spaceBetween: 20,
                    slidesOffsetBefore: 16,
                    slidesOffsetAfter: 16,
                    navigation: {
                        nextEl: '.swiper-recently-viewed-button-next',
                        prevEl: '.swiper-recently-viewed-button-prev',
                    },
                    breakpoints: {
                        1024: {
                            slidesPerView: 4.5,
                            spaceBetween: 20,
                            slidesOffsetBefore: 40,
                            slidesOffsetAfter: 40,
                        }
                    },
                    on: {
                        init: (swiper) => this.toggleNav(swiper),
                        resize: (swiper) => this.toggleNav(swiper),
                        update: (swiper) => this.toggleNav(swiper),
                        imagesReady: (swiper) => this.toggleNav(swiper),
                    }
                });
                
            } catch (error) {
                console.warn('[RV] Error initializing Swiper:', error);
            }
        }

        toggleNav(swiper) {
            const nextBtn = this.querySelector('.swiper-recently-viewed-button-next');
            const prevBtn = this.querySelector('.swiper-recently-viewed-button-prev');
            if (!nextBtn || !prevBtn) return;

            const shouldHide = swiper.isLocked === true;

            nextBtn.style.display = shouldHide ? 'none' : '';
            prevBtn.style.display = shouldHide ? 'none' : '';
        }

    }

    customElements.define(customElements.component, recentlyViewed);
}
